import React from "react";
import utils from "@/utils/utils";
import { navigateByMessagePath } from "@/utils/messagePathNavigation";
import "./NoticeContentImage.scss";

export interface NoticeContentImageProps {
  /** 后端 UserNotice.extra，含 contentImage(resourceId)/contentImageLink(点击跳转) */
  extra?: Record<string, any> | null;
  navigate: (path: string) => void;
  /** 点击时上报 trackClick（返回值忽略，允许任意 Promise） */
  onTrackClick?: () => void | Promise<unknown>;
  /** 内链跳转前的收尾（如关闭弹窗），仅弹窗场景需要 */
  onBeforeInternalNav?: () => void | Promise<void>;
  alt?: string;
}

/**
 * 站内信正文图片（trigger 下发，非必填）。
 * 点击：以 `/` 开头 = 站内跳转；否则外链新标签打开（无协议头补 https）。
 * 尺寸控制见 NoticeContentImage.scss（宽撑满容器、按原比例延伸高度）。
 */
export const NoticeContentImage: React.FC<NoticeContentImageProps> = ({
  extra,
  navigate,
  onTrackClick,
  onBeforeInternalNav,
  alt,
}) => {
  const contentImage = (extra?.contentImage as string) || "";
  const contentImageLink = ((extra?.contentImageLink as string) || "").trim();
  if (!contentImage) return null;

  const url = utils.getImageUrl(contentImage, { width: 640 });

  const handleClick = () => {
    if (!contentImageLink) return;
    void (async () => {
      try {
        if (onTrackClick) await onTrackClick();
      } catch (error) {
        console.error("track notice image click failed:", error);
      }
      if (contentImageLink.startsWith("/")) {
        // 内链：站内跳转（先做收尾，如关闭弹窗）
        if (onBeforeInternalNav) await Promise.resolve(onBeforeInternalNav());
        navigateByMessagePath(contentImageLink, navigate);
      } else {
        // 外链：新标签打开（无协议头补 https）
        const target = /^(https?:)?\/\//i.test(contentImageLink)
          ? contentImageLink
          : `https://${contentImageLink}`;
        window.open(target, "_blank", "noopener,noreferrer");
      }
    })();
  };

  return (
    <img
      className="notice-content-image"
      src={url}
      alt={alt || ""}
      onClick={contentImageLink ? handleClick : undefined}
      style={contentImageLink ? { cursor: "pointer" } : undefined}
    />
  );
};

export default NoticeContentImage;
